import {
    useComposedRef,
    useEditorPlugin,
    useEditorReadOnly,
    useFocused,
    useHotkeys,
    useOnClickOutside,
    usePluginOption,
} from '@udecode/plate/react';
import {
    getDOMSelectionBoundingClientRect,
    type UseVirtualFloatingOptions,
} from '@udecode/plate-floating';
import React from 'react';

import { LinkPlugin } from '../../LinkPlugin';
import { submitFloatingLink } from '../../transforms';
import { triggerFloatingLinkInsert } from '../../utils/triggerFloatingLinkInsert';
import { useFloatingLinkEscape } from './useFloatingLinkEscape';
import { useVirtualFloatingLink } from './useVirtualFloatingLink';

export type LinkFloatingToolbarState = {
  floatingOptions?: UseVirtualFloatingOptions;
};

export const useFloatingLinkInsertState = ({
    floatingOptions,
}: LinkFloatingToolbarState = {}) => {
    const { editor, getOptions } = useEditorPlugin(LinkPlugin);

    const { triggerFloatingLinkHotkeys } = getOptions();
    const readOnly = useEditorReadOnly();
    const focused = useFocused();
    const mode = usePluginOption(LinkPlugin, 'mode');
    const isOpen = usePluginOption(LinkPlugin, 'isOpen', editor.id);

    const floating = useVirtualFloatingLink({
        editorId: editor.id,
        getBoundingClientRect: getDOMSelectionBoundingClientRect,
        open: isOpen && mode === 'insert',
        whileElementsMounted: () => () => {},
        ...floatingOptions,
    });

    return {
        floating,
        focused,
        isOpen,
        readOnly,
        triggerFloatingLinkHotkeys,
    };
};

export const useFloatingLinkInsert = ({
    floating,
    focused,
    isOpen,
    readOnly,
    triggerFloatingLinkHotkeys,
}: ReturnType<typeof useFloatingLinkInsertState>) => {
    const { api, editor, getOptions, setOption } = useEditorPlugin(LinkPlugin);

    const onChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
        (e) => {
            setOption('text', e.target.value);
        },
        [setOption]
    );

    const ref = useOnClickOutside(
        () => {
            if (getOptions().mode === 'insert') {
                submitFloatingLink(editor);
                api.floatingLink.hide();
                editor.tf.focus({ at: editor.selection! });
            }
        },
        {
            disabled: !isOpen,
        }
    );

    // wait for update before focusing input
    React.useEffect(() => {
        if (isOpen) {
            floating.update();
            setOption('updated', true);
        } else {
            setOption('updated', false);
        }
    }, [isOpen, floating.update]);

    useHotkeys(
        triggerFloatingLinkHotkeys!,
        (e) => {
            if (triggerFloatingLinkInsert(editor, { focused })) {
                e.preventDefault();
            }
        },
        {
            enableOnContentEditable: true,
        },
        [focused]
    );

    useFloatingLinkEscape();

    const { text, updated } = getOptions();

    const updatedValue = React.useCallback(
        (el: HTMLInputElement) => {
            if (el && updated) {
                el.value = getOptions().text;
            }
        },
        [getOptions, updated]
    );

    return {
        hidden: readOnly || !isOpen,
        props: {
            style: {
                ...floating.style,
                zIndex: 50,
            },
        },
        ref: useComposedRef<HTMLDivElement>(floating.refs.setFloating, ref),
        textInputProps: {
            defaultValue: text,
            ref: updatedValue,
            onChange,
        },
    };
};
